import { AppHTTPService } from '@/api/http-service'
import {
  getConfigs,
  MentionsPopover,
} from '@/components/Mentions/MentionsPopover'
import { FieldsContext } from '@/contexts/FieldsContext'
import { useSheet } from '@/contexts/SheetContext'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  DropdownInput,
  Icon,
  Input,
  RichMentionsInput,
  RichMentionsProvider,
  Switch,
} from '@flatfile/baseline'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'

interface EnrichmentAction {
  id: string
  type: 'translate' | 'research' | 'prompt'
  config: {
    [key: string]: any
  }
}

enum EnrichmentActionType {
  Translate = 'translate',
  Research = 'research',
  Prompt = 'prompt',
}

// Placeholder AddEnrichment component
function EnrichmentPanel({
  sheetId,
  onClose,
  columnConfig,
  editEnrichmentKey,
}: {
  sheetId: string
  onClose: () => void
  columnConfig: any[]
  editEnrichmentKey?: string
}) {
  const { sheet } = useSheet()
  const [isEditMode, setIsEditMode] = useState(!!editEnrichmentKey)

  // Reset form when switching between add/edit modes
  useEffect(() => {
    setIsEditMode(!!editEnrichmentKey)

    // If switching to add mode (no editEnrichmentKey), reset form
    if (!editEnrichmentKey) {
      setNewDestinationColumnName('')
      setDestinationField('')
      setAdvancedSettings({
        requiredInputs: false,
        keywords: false,
        location: false,
        createNewColumn: true,
        runSettings: false,
        runInBackground: false,
      })
      setActions([
        {
          id: 'action-1',
          type: 'research',
          config: {
            prompt: '',
          },
        },
      ])
    }
  }, [editEnrichmentKey])

  // State for selected source fields (array of column keys)
  // const [sourceFields, setSourceFields] = useState<string[]>(
  //   columnConfig && columnConfig.length > 0 ? [columnConfig[0].value] : []
  // )

  // State for the destination field - for selecting an existing column
  const [destinationField, setDestinationField] = useState<string>(
    // Initially empty as createNewColumn will be true by default
    ''
  )
  // State for the new destination column name - for creating a new column
  const [newDestinationColumnName, setNewDestinationColumnName] =
    useState<string>('')

  // State for actions with their type-specific configurations
  const [actions, setActions] = useState<EnrichmentAction[]>([
    {
      id: 'action-1',
      type: 'research',
      config: {
        prompt: '',
      },
    },
  ])

  // Load existing enrichment configuration if in edit mode
  useEffect(() => {
    if (editEnrichmentKey && sheet?.enrichments) {
      console.time(`enrichment-component-load-${editEnrichmentKey}`)

      // Find the enrichment in the sheet data (no API call needed!)
      const enrichment = sheet.enrichments.find(
        (e) => e.key === editEnrichmentKey
      )

      if (enrichment) {
        // Set the target column as the new column name (since we're editing an existing enrichment)
        if (enrichment.config.targetColumn) {
          setNewDestinationColumnName(enrichment.config.targetColumn)
          setDestinationField(enrichment.config.targetColumn)
        }

        // Set advanced settings including background enrichment and createNewColumn
        setAdvancedSettings((prev) => ({
          ...prev,
          runInBackground: enrichment.runInBackground || false,
          createNewColumn: true, // Since we're editing, assume it was created as new column
        }))

        // Create action from existing enrichment
        const action: EnrichmentAction = {
          id: enrichment.id || enrichment.key,
          type: enrichment.type,
          config: enrichment.config,
        }

        setActions([action])
        console.timeEnd(`enrichment-component-load-${editEnrichmentKey}`)
      } else {
        console.error('Enrichment not found in sheet data:', editEnrichmentKey)
        console.timeEnd(`enrichment-component-load-${editEnrichmentKey}`)
      }
    }
  }, [editEnrichmentKey, sheet?.enrichments])

  // Available languages for translation
  const languageOptions = [
    { label: 'Spanish', value: 'es' },
    { label: 'French', value: 'fr' },
    { label: 'German', value: 'de' },
    { label: 'Japanese', value: 'ja' },
    { label: 'Chinese (Simplified)', value: 'zh-CN' },
    { label: 'Portuguese', value: 'pt' },
    { label: 'Italian', value: 'it' },
    { label: 'Russian', value: 'ru' },
    { label: 'Korean', value: 'ko' },
    { label: 'Arabic', value: 'ar' },
  ]

  // State for advanced settings
  const [advancedSettings, setAdvancedSettings] = useState({
    requiredInputs: false,
    keywords: false,
    location: false,
    createNewColumn: true, // Default to creating a new column
    runSettings: false,
    runInBackground: false, // New setting for background enrichment
  })

  // // Add a new field (first available not already selected)
  // function handleAddField() {
  //   const available = columnConfig.find(
  //     (col) => !sourceFields.includes(col.value)
  //   )
  //   if (available) {
  //     setSourceFields([...sourceFields, available.value])
  //   }
  // }

  // // Remove a field by index
  // function handleRemoveField(idx: number) {
  //   setSourceFields(sourceFields.filter((_, i) => i !== idx))
  // }

  // // Change a field selection
  // function handleSourceFieldChange(idx: number, newValue: string) {
  //   setSourceFields(sourceFields.map((val, i) => (i === idx ? newValue : val)))
  // }

  const { mentions } = useContext(FieldsContext)

  const configs = useMemo(() => {
    return mentions.length > 0 ? getConfigs(mentions) : []
  }, [mentions])

  // Use a reference to track if mentions have changed
  const prevMentionsRef = useRef(mentions)
  const mentionsContextRef = useRef(null)

  // Add an effect to update configs in the context when mentions change
  useEffect(() => {
    // Check if mentions have actually changed (comparing refs isn't reliable)
    const mentionsChanged =
      JSON.stringify(prevMentionsRef.current) !== JSON.stringify(mentions)

    if (mentionsChanged && mentionsContextRef.current?.configs) {
      // Update the configs in the RichMentionsContext
      mentionsContextRef.current.configs = configs

      // Update our reference for next comparison
      prevMentionsRef.current = mentions
    }
  }, [mentions, configs])

  // Keep the stable key to prevent unnecessary remounts
  const mentionsKey = `mentions-provider-instance`

  // Toggle a setting
  function handleToggleSetting(setting: keyof typeof advancedSettings) {
    const newSettingValue = !advancedSettings[setting]
    setAdvancedSettings({
      ...advancedSettings,
      [setting]: newSettingValue,
    })

    if (setting === 'createNewColumn') {
      if (newSettingValue) {
        // Switched TO creating a new column
        setDestinationField('') // Clear selected existing field
      } else {
        // Switched FROM creating a new column (i.e., to selecting an existing one)
        setNewDestinationColumnName('') // Clear new field name
        // Set destinationField to the first available column, if any
        if (columnConfig && columnConfig.length > 0) {
          setDestinationField(columnConfig[0].value)
        } else {
          setDestinationField('')
        }
      }
    }
  }

  // Add a new action
  function handleAddAction(
    type: 'translate' | 'research' | 'prompt' = 'translate'
  ) {
    const newAction: EnrichmentAction = {
      id: `action-${Date.now()}`,
      type,
      config:
        type === 'translate'
          ? { languageSource: 'fixed', language: 'es', languageField: '' }
          : { prompt: '' },
    }

    setActions([...actions, newAction])
  }

  // Remove an action by index
  function handleRemoveAction(idx: number) {
    setActions(actions.filter((_, i) => i !== idx))
  }

  // Update action type
  function handleChangeActionType(
    idx: number,
    newType: 'translate' | 'research' | 'prompt'
  ) {
    setActions(
      actions.map((action, i) => {
        if (i === idx) {
          return {
            ...action,
            type: newType,
            config:
              newType === 'translate'
                ? { languageSource: 'fixed', language: 'es', languageField: '' }
                : { prompt: '' },
          }
        }
        return action
      })
    )
  }

  // Update action configuration
  function handleUpdateActionConfig(idx: number, configUpdate: any) {
    setActions(
      actions.map((action, i) => {
        if (i === idx) {
          return {
            ...action,
            config: {
              ...action.config,
              ...configUpdate,
            },
          }
        }
        return action
      })
    )
  }

  // Render action-specific configuration inputs
  function renderActionConfig(action: EnrichmentAction, idx: number) {
    if (action.type === 'translate') {
      return (
        <div className='mt-2 ml-2 space-y-3'>
          <div>
            <div className='flex mb-1'>
              <label className='text-sm font-medium text-gray-700'>
                Language Source
              </label>
            </div>
            <DropdownInput
              options={[
                { label: 'Fixed Language', value: 'fixed' },
                { label: 'From Field', value: 'field' },
              ]}
              value={[action.config.languageSource]}
              onValueChange={([newValue]) =>
                handleUpdateActionConfig(idx, { languageSource: newValue })
              }
              className='w-full'
            />
          </div>

          {action.config.languageSource === 'fixed' ? (
            <div>
              <div className='flex mb-1'>
                <label className='text-sm font-medium text-gray-700'>
                  Target Language
                </label>
              </div>
              <DropdownInput
                options={languageOptions}
                value={[action.config.language]}
                onValueChange={([newValue]) =>
                  handleUpdateActionConfig(idx, { language: newValue })
                }
                className='w-full'
              />
            </div>
          ) : (
            <div>
              <div className='flex mb-1'>
                <label className='text-sm font-medium text-gray-700'>
                  Language Field
                </label>
              </div>
              <DropdownInput
                options={columnConfig.map((col) => ({
                  label: col.label,
                  value: col.value,
                }))}
                value={[action.config.languageField]}
                onValueChange={([newValue]) =>
                  handleUpdateActionConfig(idx, { languageField: newValue })
                }
                className='w-full'
              />
            </div>
          )}
        </div>
      )
    } else if (action.type === 'research' || action.type === 'prompt') {
      return (
        <div className='mt-2'>
          <div className='flex mb-1'>
            <label className='text-sm font-medium text-gray-700'>
              {action.type === 'research' ? 'Research Prompt' : 'Prompt'}
            </label>
          </div>
          <RichMentionsProvider
            configs={configs as any}
            key={mentionsKey}
            getContext={mentionsContextRef}
          >
            <RichMentionsInput
              value={action.config.prompt}
              className={`w-full min-h-[100px] border border-gray-300 rounded-md bg-white px-3 py-2 text-base placeholder:text-gray-400
                focus:outline-none overflow-y-auto `}
              placeholder='Translate @Notes...'
              disabled={false}
              onInput={(event) =>
                handleUpdateActionConfig(idx, {
                  prompt: event.currentTarget.textContent,
                })
              }
              spellCheck={false}
            />
            <MentionsPopover />
          </RichMentionsProvider>
        </div>
      )
    }

    return null
  }

  // TODO: Runs the call immediately and not on each change
  const onRun = async () => {
    const finalDestinationFieldKey = advancedSettings.createNewColumn
      ? newDestinationColumnName
      : destinationField

    const enrichment = {
      name: finalDestinationFieldKey + ' ' + actions[0]?.type,
      type: actions[0]?.type,
      key: finalDestinationFieldKey,
      runInBackground: advancedSettings.runInBackground,
      config: {
        prompt: actions[0]?.config?.prompt || '',
        provider: 'exa',
        targetColumn: finalDestinationFieldKey,
      },
    }

    if (advancedSettings.runInBackground) {
      const { data: currentEnrichments } =
        await AppHTTPService.client.sheets.get(sheetId)
      const updatedEnrichments =
        currentEnrichments.enrichments?.length > 0
          ? [...currentEnrichments.enrichments, enrichment]
          : [enrichment]
      const result = await AppHTTPService.client.sheets.update(sheetId, {
        enrichments: updatedEnrichments,
      })
    }

    let result
    if (actions[0]?.type === 'prompt') {
      result = await AppHTTPService.client.enrichments.prompt({
        sheetId,
        enrichment,
        columnConfig,
      })
    } else if (actions[0]?.type === 'research') {
      result = await AppHTTPService.client.enrichments.researchInngest({
        sheetId,
        enrichment,
        columnConfig,
      })
    }
  }

  return (
    <div className='w-[424px] h-full bg-white shadow-xl p-6 flex flex-col'>
      <div className='flex justify-between items-center mb-4'>
        <h2 className='text-base font-medium text-gray-800'>
          {isEditMode ? 'Edit Enrichment' : 'Add Enrichment'}
        </h2>
        <button onClick={onClose} className='text-gray-500 hover:text-black'>
          ✕
        </button>
      </div>

      <div className='flex-1 overflow-y-auto'>
        <div className='mb-4'>
          <h2 className='text-lg font-semibold mb-4'>Enrich Data</h2>
          <p className='text-sm text-gray-600 mb-6'>
            Automatically enriches records with useful data points. Enriched
            data removes the need for manually sourcing and adding data to your
            records.
          </p>
        </div>

        {/* Show form - data comes instantly from sheet context */}
        <Accordion
          type='multiple'
          defaultValue={['actions', 'destinationField', 'advanced']}
          className='flex flex-col gap-3'
        >
          {/* Source Fields Section */}
          {/* <AccordionItem value='sourceFields' className='border-0'>
            <AccordionTrigger className='py-2'>
              <span className='text-sm font-medium'>Source Field(s)</span>
            </AccordionTrigger>
            <AccordionContent className='pt-2 overflow-hidden'>
              <div className='flex flex-col gap-2 mb-2'>
                {sourceFields.map((field, idx) => (
                  <div key={idx} className='flex items-center gap-2'>
                    <DropdownInput
                      options={columnConfig.map((col) => ({
                        label: col.label,
                        value: col.value,
                        disabled:
                          sourceFields.includes(col.value) &&
                          col.value !== field,
                      }))}
                      value={[field]}
                      onValueChange={([newValue]) =>
                        handleSourceFieldChange(idx, newValue as string)
                      }
                      placeholder='Select field'
                      matchTriggerWidth={false}
                      className='flex-1 w-full'
                    />
                    <button
                      className='text-gray-400 hover:text-red-500 px-2'
                      onClick={() => handleRemoveField(idx)}
                      aria-label='Remove field'
                    >
                      <Icon name='trash-can-2' />
                    </button>
                  </div>
                ))}
              </div>
              <div className='flex gap-2 mt-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => handleAddField()}
                  className='w-full'
                >
                  Add Field
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem> */}

          {/* Destination Field Section */}
          <AccordionItem value='destinationField' className='border-0'>
            <AccordionTrigger className='py-2'>
              <span className='text-sm font-medium'>Destination Field</span>
            </AccordionTrigger>
            <AccordionContent className='pt-2 overflow-hidden'>
              <div className='flex flex-col gap-4 mt-2'>
                <div className='flex justify-between items-center'>
                  <label htmlFor='create-new-column' className='text-gray-600'>
                    Create new column
                  </label>
                  <Switch
                    id='create-new-column'
                    checked={advancedSettings.createNewColumn}
                    onCheckedChange={() =>
                      handleToggleSetting('createNewColumn')
                    }
                  />
                </div>

                {advancedSettings.createNewColumn ? (
                  <div>
                    <div className='flex mb-1'>
                      <label
                        htmlFor='new-destination-column-name'
                        className='text-sm font-medium text-gray-700'
                      >
                        New Column Name
                      </label>
                    </div>
                    <Input // Using TextArea for new column name
                      id='new-destination-column-name'
                      value={newDestinationColumnName}
                      onChange={(e) =>
                        setNewDestinationColumnName(e.target.value)
                      }
                      placeholder='Enter name for the new column'
                      className='w-full'
                    />
                  </div>
                ) : (
                  <div>
                    <div className='flex mb-1'>
                      <label
                        htmlFor='destination-field-select'
                        className='text-sm font-medium text-gray-700'
                      >
                        Select Destination Column
                      </label>
                    </div>
                    <DropdownInput
                      id='destination-field-select'
                      options={columnConfig.map((col) => ({
                        label: col.label,
                        value: col.value,
                      }))}
                      value={[destinationField]}
                      onValueChange={([newValue]) =>
                        setDestinationField(newValue as string)
                      }
                      placeholder='Select destination column'
                      className='w-full'
                    />
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Actions Section */}
          <AccordionItem value='actions' className='border-0'>
            <AccordionTrigger className='py-2'>
              <span className='text-sm font-medium'>Actions</span>
            </AccordionTrigger>
            <AccordionContent className='pt-2 overflow-hidden'>
              <div className='flex flex-col gap-4 mb-2'>
                {actions.map((action, idx) => (
                  <div key={action.id} className='flex flex-col'>
                    <div className='flex items-center gap-2'>
                      <DropdownInput
                        options={[
                          {
                            label: 'Research',
                            value: EnrichmentActionType.Research,
                          },
                          // TODO: Add back in when we make the translate enrichment
                          // { label: 'Translate', value: EnrichmentActionType.Translate },
                          {
                            label: 'Prompt',
                            value: EnrichmentActionType.Prompt,
                          },
                        ]}
                        value={[action.type]}
                        onValueChange={([newValue]) => {
                          handleChangeActionType(
                            idx,
                            newValue as EnrichmentActionType
                          )
                        }}
                        placeholder='Select action type'
                        matchTriggerWidth={false}
                        className='flex-1 w-full'
                      />
                      <button
                        className='text-gray-400 hover:text-red-500 px-2'
                        onClick={() => handleRemoveAction(idx)}
                        aria-label='Remove action'
                      >
                        <Icon name='trash-can-2' />
                      </button>
                    </div>

                    {/* Render specific configuration based on action type */}
                    {renderActionConfig(action, idx)}
                  </div>
                ))}
              </div>

              {/* <div className='flex gap-2 mt-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => handleAddAction()}
                  className='w-full'
                >
                  Add Action
                </Button>
              </div> */}
            </AccordionContent>
          </AccordionItem>

          {/* Advanced column settings */}
          <AccordionItem value='advanced' className='border-0'>
            <AccordionTrigger className='py-2'>
              <span className='text-sm font-medium'>Advanced settings</span>
            </AccordionTrigger>
            <AccordionContent className='pt-2 overflow-hidden'>
              <div className='flex flex-col gap-4 mt-2'>
                <div className='flex justify-between items-center'>
                  <div className='flex flex-col gap-1'>
                    <label
                      htmlFor='run-in-background'
                      className='text-gray-700 font-medium'
                    >
                      Run on update
                    </label>
                    <span className='text-xs text-gray-500'>
                      Automatically run this enrichment when records are updated
                    </span>
                  </div>
                  <Switch
                    id='run-in-background'
                    checked={advancedSettings.runInBackground}
                    onCheckedChange={() =>
                      handleToggleSetting('runInBackground')
                    }
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Footer with actions */}
      <div className='flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200'>
        <Button variant='outline' onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => onRun()}>Run</Button>
      </div>
    </div>
  )
}

export default EnrichmentPanel
